import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import MoreButton from "@/components/MoreButton/MoreButton";
import { DeleteOutlined, EditOutlined, MoreOutlined, SettingOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";

import StatusTag from "@/components/StatusTag/StatusTag";
import Table, { Column } from "@/components/Table/Table";
import {
  ProductEol,
  useGetProductEol,
  useProductEolStatusPerYear,
} from "@/pages/products/eol/useEol";

import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import FakeLink from "@/components/FakeLink/FakeLink";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeaderNew } from "@/components/PageHeader/PageHeaderNew";
import StatusTags from "@/components/StatusTags/StatusTags";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import useAnalytics from "@/hooks/useAnalytics";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useTranslation from "@/hooks/useTranslation";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import styles from "@/pages/products/bom/BillOfMaterials.module.scss";
import DeleteEolAction from "@/pages/products/eol/DeleteEolAction";
import EolImpactModal from "@/pages/products/eol/EolImpactModal";
import { Button, Dropdown, DropdownProps, Space, Tooltip } from "antd";
import { PRODUCTS_BREADCRUMBS } from "../breadcrumbs";
import HiddenProductsMessage from "../HiddenProductsMessage";

function EolOverview() {
  useTrackOnLoad("EOL viewed");

  const analytics = useAnalytics();
  const { year } = useDashboardYear();
  const translation = useTranslation();

  const [selectedProducts, setSelectedProducts] = useState<ProductEol[]>([]);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState<boolean>(false);
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const { data: productsYears } = useProductEolStatusPerYear();

  const handleOpenChange: DropdownProps["onOpenChange"] = (nextOpen, info) => {
    if (info.source === "trigger" || nextOpen) {
      setMoreDropdownOpen(nextOpen);
    }
  };

  const columns: Column<ProductEol>[] = [
    {
      key: "external_id",
      label: "ID",
      searchable: true,
      sortable: true,
      minWidth: "115px",
      maxWidth: "200px",
    },
    {
      key: "name",
      label: "Product",
      searchable: true,
      sortable: true,
      minWidth: "200px",
      maxWidth: "300px",
    },
    {
      key: "category",
      label: "Category",
      sortable: true,
      searchable: true,
      minWidth: "165px",
      renderer: (product) => product.category || "-",
    },
    {
      key: "dataset_name",
      label: "End of life",
      sortable: true,
      searchable: true,
      minWidth: "175px",
      renderer: (product) => product.match?.name || "-",
    },
    {
      key: "status",
      label: "Status",
      minWidth: "150px",
      maxWidth: "150px",
      sortable: true,
      renderer: (product) => (
        <StatusTag
          status={product.status}
          showIcon={false}
        />
      ),
      filterable: true,
    },
    {
      key: "actions",
      label: "Actions",
      minWidth: "80px",
      maxWidth: "80px",
      noLink: true,
      renderer: (product) => (
        <ActionsPopover
          actions={
            <>
              <EolImpactModal
                selectedProducts={[product]}
                onSuccess={() => setSelectedProducts([])}
              >
                <ActionsPopOverItem icon={<EditOutlined />}>Edit end of life</ActionsPopOverItem>
              </EolImpactModal>
              <DeleteEolAction
                selectedProducts={[product]}
                onSuccess={() => null}
              >
                <ActionsPopOverItem
                  icon={<DeleteOutlined />}
                  danger
                >
                  Delete end of life reference
                </ActionsPopOverItem>
              </DeleteEolAction>
            </>
          }
        >
          <MoreOutlined />
        </ActionsPopover>
      ),
      align: "end",
    },
  ];

  const onSelectedKeysChange = (keys: number[]) => {
    analytics.track("Product Selected");
    setSelectedProducts((currentProducts) => {
      let productsOnOtherPages = [
        ...currentProducts.filter((x) => !data?.data.map((x) => x.id).includes(x.id)),
      ];
      return [
        ...productsOnOtherPages,
        ...(data?.data.filter((product) => keys.includes(product.id)) || []),
      ];
    });
  };

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "external_id", order: "ascend" },
    });

  const { data, isLoading, isFetching } = useGetProductEol({
    pagination,
    filter,
    search,
    sort,
    year,
    showUnrelated,
    enabled: !!year,
  });

  useEffect(() => {
    setMoreDropdownOpen(false);
  }, [isFetching]);

  const resetTable = () => {
    setPage(1);
    setSelectedProducts([]);
  };

  return (
    <div>
      <PageHeaderNew
        title={translation.eol.overview.pageTitle}
        description={translation.eol.overview.pageDescription}
        breadcrumbs={PRODUCTS_BREADCRUMBS}
        titleRight={
          <>
            <MoreButton
              actions={
                <ActionsPopOverItem
                  to="/impact_references"
                  icon={<SettingOutlined />}
                >
                  Manage impact references
                </ActionsPopOverItem>
              }
            />
          </>
        }
      />
      <TableHeader
        noRoundedTop
        left={
          <>
            {selectedProducts.length > 0 ? (
              <span className={styles["num-selected"]}>{selectedProducts.length} selected:</span>
            ) : (
              <YearSelector
                disableYears={showUnrelated}
                optionData={productsYears}
                onChange={resetTable}
                right={
                  <Tooltip
                    placement="bottom"
                    title="Show products that are not related to purchase or sales orders"
                  >
                    <>
                      <SwitchWithLabel
                        togglePosition="right"
                        size="small"
                        checked={showUnrelated}
                        onChange={(disabled) => {
                          resetTable();
                          setShowUnrelated(disabled);
                        }}
                      >
                        Year unrelated
                      </SwitchWithLabel>
                    </>
                  </Tooltip>
                }
              />
            )}
            {selectedProducts.length > 0 && (
              <PageButtonGroup loading={isLoading}>
                <EolImpactModal
                  selectedProducts={selectedProducts}
                  onSuccess={() => setSelectedProducts([])}
                >
                  <Button icon={<EditOutlined />}>
                    {translation.eol.overview.addReferenceText}
                  </Button>
                </EolImpactModal>
                <Dropdown
                  trigger={["click"]}
                  onOpenChange={handleOpenChange}
                  open={moreDropdownOpen}
                  menu={{
                    items: [
                      {
                        label: (
                          <DeleteEolAction
                            selectedProducts={selectedProducts}
                            onSuccess={() => null}
                          >
                            <Space>
                              <DeleteOutlined />
                              <span>{translation.eol.overview.deleteReferenceText}</span>
                            </Space>
                          </DeleteEolAction>
                        ),
                        key: "1",
                        danger: true,
                      },
                    ],
                  }}
                >
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              </PageButtonGroup>
            )}
            {selectedProducts.length > 0 && (
              <FakeLink onClick={() => setSelectedProducts([])}>Clear selection</FakeLink>
            )}
            <HiddenProductsMessage />
          </>
        }
        right={
          <StatusTags
            data={data}
            loading={isLoading}
          />
        }
      />
      <Table<ProductEol>
        availableFilters={{
          status: [
            { label: "Complete", value: "complete" },
            { label: "Incomplete", value: "incomplete" },
          ],
        }}
        loading={isLoading || isFetching}
        rowKey="id"
        checkable={true}
        data={data?.data}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        onSelectedKeysChange={onSelectedKeysChange}
        selectedKeys={selectedProducts.map((x) => x.id)}
        totalRows={data?.total_count}
        pagination={pagination}
        sort={sort}
        search={search}
        filter={filter}
        columns={columns}
        rowLink={(record) => `/products/${record.id}/eol`}
      />
    </div>
  );
}

export default EolOverview;
